/** Placeholder furniture and fish art, drawn in code like the avatars. */
import { drawGhost } from './ghost';

type Render = Record<string, unknown>;

const shade = (hex: string, amount: number) => {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(v + amount * 255)));
  return `#${((ch((n >> 16) & 255) << 16) | (ch((n >> 8) & 255) << 8) | ch(n & 255)).toString(16).padStart(6, '0')}`;
};

export const furnitureSize = (r: Render) => ({ w: (r.w as number) ?? 60, h: (r.h as number) ?? 60 });

/** Draws furniture centred on (0,0), already rotated by the caller if needed. */
export function drawFurniture(c: CanvasRenderingContext2D, r: Render) {
  const color = (r.color as string) ?? '#b98a5e';
  const { w, h } = furnitureSize(r);
  const dark = shade(color, -0.2);
  c.lineJoin = 'round';
  switch (r.shape) {
    case 'rug':
      c.fillStyle = color;
      c.beginPath();
      c.ellipse(0, 0, w / 2, h / 2, 0, 0, Math.PI * 2);
      c.fill();
      c.strokeStyle = shade(color, 0.15);
      c.lineWidth = 4;
      c.beginPath();
      c.ellipse(0, 0, w / 2 - 10, h / 2 - 8, 0, 0, Math.PI * 2);
      c.stroke();
      break;
    case 'chair':
      c.fillStyle = dark;
      c.fillRect(-w / 2 + 4, -h / 2, w - 8, h * 0.45);
      c.fillStyle = color;
      c.beginPath();
      c.roundRect(-w / 2, -h * 0.1, w, h * 0.35, 6);
      c.fill();
      c.fillStyle = dark;
      c.fillRect(-w / 2 + 4, h * 0.25, 6, h * 0.25);
      c.fillRect(w / 2 - 10, h * 0.25, 6, h * 0.25);
      break;
    case 'lamp':
      c.fillStyle = '#8a5a3b';
      c.fillRect(-3, -h * 0.1, 6, h * 0.55);
      c.fillStyle = '#8a5a3b';
      c.fillRect(-w / 2 + 6, h * 0.42, w - 12, 6);
      c.fillStyle = color;
      c.beginPath();
      c.moveTo(-w / 2, -h * 0.05);
      c.quadraticCurveTo(0, -h * 0.75, w / 2, -h * 0.05);
      c.closePath();
      c.fill();
      c.fillStyle = 'rgba(255,230,150,0.35)';
      c.beginPath();
      c.arc(0, -h * 0.2, w * 0.7, 0, Math.PI * 2);
      c.fill();
      break;
    case 'plant':
      c.fillStyle = '#c4703f';
      c.beginPath();
      c.roundRect(-w * 0.3, h * 0.1, w * 0.6, h * 0.4, 4);
      c.fill();
      c.fillStyle = color;
      for (let i = -2; i <= 2; i++) {
        c.beginPath();
        c.ellipse(i * 6, -h * 0.1, 6, h * 0.32, i * 0.35, 0, Math.PI * 2);
        c.fill();
      }
      break;
    case 'shelf':
      c.fillStyle = color;
      c.fillRect(-w / 2, -h / 2, w, h);
      c.fillStyle = dark;
      for (let i = 1; i < 4; i++) c.fillRect(-w / 2 + 4, -h / 2 + (i * h) / 4, w - 8, 4);
      ['#d4553e', '#16808f', '#e9a915', '#7a4f9a'].forEach((b, i) => {
        c.fillStyle = b;
        c.fillRect(-w / 2 + 8 + i * 12, -h / 2 + 6, 9, h / 4 - 8);
      });
      break;
    case 'tank':
      c.fillStyle = '#8a5a3b';
      c.fillRect(-w / 2, h * 0.3, w, h * 0.2);
      c.fillStyle = color;
      c.globalAlpha = 0.75;
      c.fillRect(-w / 2 + 4, -h / 2, w - 8, h * 0.8);
      c.globalAlpha = 1;
      drawFish(c, '#e9a915', 0.6, -10, -h * 0.1);
      drawFish(c, '#f08aa8', 0.5, 16, h * 0.05);
      break;
    case 'lantern':
      c.fillStyle = '#1d2b36';
      c.fillRect(-1, -h / 2, 2, h * 0.15);
      c.fillStyle = color;
      c.beginPath();
      c.ellipse(0, 0, w / 2, h * 0.36, 0, 0, Math.PI * 2);
      c.fill();
      c.strokeStyle = dark;
      c.lineWidth = 1.5;
      for (const k of [-0.5, 0, 0.5]) {
        c.beginPath();
        c.ellipse(0, 0, Math.abs(k) * w * 0.5 || 1, h * 0.36, 0, 0, Math.PI * 2);
        c.stroke();
      }
      c.fillStyle = 'rgba(255,230,150,0.35)';
      c.beginPath();
      c.arc(0, 0, w * 0.75, 0, Math.PI * 2);
      c.fill();
      break;
    case 'pumpkin':
      for (const dx of [-w * 0.22, w * 0.22, 0]) {
        c.fillStyle = dx === 0 ? color : dark;
        c.beginPath();
        c.ellipse(dx, h * 0.08, w * 0.3, h * 0.38, 0, 0, Math.PI * 2);
        c.fill();
      }
      c.fillStyle = '#4f7a2a';
      c.fillRect(-3, -h * 0.45, 6, h * 0.18);
      if (r.face) {
        c.fillStyle = '#fff3a0';
        c.beginPath();
        c.moveTo(-w * 0.22, 0); c.lineTo(-w * 0.08, 0); c.lineTo(-w * 0.15, -h * 0.14);
        c.moveTo(w * 0.08, 0); c.lineTo(w * 0.22, 0); c.lineTo(w * 0.15, -h * 0.14);
        c.fill();
        c.beginPath();
        c.moveTo(-w * 0.2, h * 0.14); c.lineTo(w * 0.2, h * 0.14); c.lineTo(0, h * 0.26);
        c.fill();
      }
      break;
    case 'cauldron': {
      const accent = (r.accent as string) ?? '#7fdc6a';
      c.fillStyle = accent;
      for (const [bx, by, br] of [[-w * 0.15, -h * 0.32, 6], [w * 0.1, -h * 0.4, 5], [w * 0.2, -h * 0.28, 4]] as const) {
        c.beginPath();
        c.arc(bx, by, br, 0, Math.PI * 2);
        c.fill();
      }
      c.fillStyle = color;
      c.beginPath();
      c.ellipse(0, h * 0.05, w * 0.45, h * 0.38, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = accent;
      c.beginPath();
      c.ellipse(0, -h * 0.2, w * 0.4, h * 0.1, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = dark;
      c.fillRect(-w * 0.35, h * 0.38, 8, h * 0.1);
      c.fillRect(w * 0.35 - 8, h * 0.38, 8, h * 0.1);
      break;
    }
    case 'tombstone':
      c.fillStyle = color;
      c.beginPath();
      c.moveTo(-w * 0.4, h * 0.45);
      c.lineTo(-w * 0.4, -h * 0.15);
      c.arc(0, -h * 0.15, w * 0.4, Math.PI, 0);
      c.lineTo(w * 0.4, h * 0.45);
      c.closePath();
      c.fill();
      c.fillStyle = dark;
      c.fillRect(-w * 0.2, -h * 0.12, w * 0.4, 3);
      c.fillRect(-w * 0.2, -h * 0.02, w * 0.3, 3);
      c.fillStyle = '#4f7a2a';
      c.fillRect(-w * 0.5, h * 0.42, w, h * 0.08);
      break;
    case 'ghost':
      drawGhost(c, 0, 0, Math.min(w, h) * 0.42);
      break;
    case 'scarecrow': {
      const accent = (r.accent as string) ?? '#d4553e';
      c.fillStyle = '#8a5a3b';
      c.fillRect(-3, -h * 0.2, 6, h * 0.7);
      c.fillRect(-w * 0.45, -h * 0.12, w * 0.9, 5);
      c.fillStyle = accent;
      c.fillRect(-w * 0.22, -h * 0.15, w * 0.44, h * 0.32);
      c.fillStyle = '#f2d39a';
      c.beginPath();
      c.arc(0, -h * 0.3, w * 0.16, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = color;
      c.beginPath();
      c.ellipse(0, -h * 0.4, w * 0.3, 4, 0, 0, Math.PI * 2);
      c.fill();
      c.fillRect(-w * 0.12, -h * 0.5, w * 0.24, h * 0.1);
      break;
    }
    case 'wheelbarrow':
      c.fillStyle = dark;
      c.fillRect(-w * 0.45, h * 0.05, w * 0.9, 4);
      c.fillStyle = color;
      c.beginPath();
      c.moveTo(-w * 0.35, -h * 0.25);
      c.lineTo(w * 0.35, -h * 0.25);
      c.lineTo(w * 0.22, h * 0.15);
      c.lineTo(-w * 0.22, h * 0.15);
      c.closePath();
      c.fill();
      c.fillStyle = '#7a5235';
      c.beginPath();
      c.ellipse(0, -h * 0.25, w * 0.32, 5, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#1d2b36';
      c.beginPath();
      c.arc(w * 0.3, h * 0.3, h * 0.16, 0, Math.PI * 2);
      c.fill();
      break;
    case 'windmill': {
      const accent = (r.accent as string) ?? '#d4553e';
      c.fillStyle = color;
      c.beginPath();
      c.moveTo(-w * 0.2, h * 0.5);
      c.lineTo(w * 0.2, h * 0.5);
      c.lineTo(w * 0.1, -h * 0.15);
      c.lineTo(-w * 0.1, -h * 0.15);
      c.closePath();
      c.fill();
      c.fillStyle = accent;
      c.fillRect(-w * 0.06, h * 0.25, w * 0.12, h * 0.25);
      c.fillStyle = dark;
      for (let i = 0; i < 4; i++) {
        c.save();
        c.translate(0, -h * 0.18);
        c.rotate((i * Math.PI) / 2 + 0.4);
        c.fillRect(-3, 0, 6, h * 0.32);
        c.restore();
      }
      break;
    }
    case 'tractor':
      c.fillStyle = color;
      c.fillRect(-w * 0.4, -h * 0.1, w * 0.6, h * 0.3);
      c.fillRect(-w * 0.05, -h * 0.4, w * 0.3, h * 0.35);
      c.fillStyle = '#bfe7f0';
      c.fillRect(0, -h * 0.34, w * 0.2, h * 0.18);
      c.fillStyle = '#1d2b36';
      c.beginPath();
      c.arc(w * 0.2, h * 0.25, h * 0.25, 0, Math.PI * 2);
      c.arc(-w * 0.3, h * 0.3, h * 0.17, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = color;
      c.beginPath();
      c.arc(w * 0.2, h * 0.25, h * 0.1, 0, Math.PI * 2);
      c.fill();
      break;
    case 'table':
      c.fillStyle = '#8a5a3b';
      c.fillRect(-4, -h * 0.05, 8, h * 0.5);
      c.fillRect(-w * 0.25, h * 0.4, w * 0.5, 5);
      c.fillStyle = color;
      c.beginPath();
      c.ellipse(0, -h * 0.1, w / 2, h * 0.28, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = 'rgba(255,255,255,0.45)';
      for (const [dx, dy] of [[-12, -6], [10, -14], [16, 2]]) {
        c.beginPath();
        c.arc(dx!, -h * 0.1 + dy!, 5, 0, Math.PI * 2);
        c.fill();
      }
      break;
    case 'beanbag':
      c.fillStyle = color;
      c.beginPath();
      c.ellipse(0, h * 0.1, w / 2, h * 0.38, 0, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = dark;
      c.beginPath();
      c.ellipse(-w * 0.08, -h * 0.05, w * 0.28, h * 0.2, -0.3, 0, Math.PI * 2);
      c.fill();
      break;
    case 'chest':
      c.fillStyle = color;
      c.fillRect(-w / 2, -h * 0.15, w, h * 0.65);
      c.fillStyle = dark;
      c.beginPath();
      c.roundRect(-w / 2, -h / 2, w, h * 0.4, [14, 14, 0, 0]);
      c.fill();
      c.fillStyle = '#e9a915';
      c.fillRect(-w / 2, -h * 0.15, w, 5);
      c.fillRect(-5, -h * 0.2, 10, 16);
      break;
    case 'boat':
      c.fillStyle = '#8a5a3b';
      c.beginPath();
      c.moveTo(-w / 2, h * 0.15);
      c.lineTo(w / 2, h * 0.15);
      c.lineTo(w * 0.3, h * 0.45);
      c.lineTo(-w * 0.3, h * 0.45);
      c.closePath();
      c.fill();
      c.fillStyle = '#1d2b36';
      c.fillRect(-2, -h / 2, 4, h * 0.65);
      c.fillStyle = '#ffffff';
      c.beginPath();
      c.moveTo(3, -h / 2);
      c.lineTo(w * 0.38, h * 0.08);
      c.lineTo(3, h * 0.08);
      c.closePath();
      c.fill();
      c.fillStyle = color;
      c.fillRect(6, -h * 0.25, w * 0.15, 5);
      c.fillRect(6, -h * 0.08, w * 0.25, 5);
      break;
    default:
      c.fillStyle = color;
      c.fillRect(-w / 2, -h / 2, w, h);
  }
}

export function drawFish(c: CanvasRenderingContext2D, color: string, scale = 1, x = 0, y = 0) {
  c.save();
  c.translate(x, y);
  c.scale(scale, scale);
  c.fillStyle = color;
  c.beginPath();
  c.ellipse(0, 0, 18, 10, 0, 0, Math.PI * 2);
  c.fill();
  c.beginPath();
  c.moveTo(14, 0);
  c.lineTo(28, -10);
  c.lineTo(28, 10);
  c.closePath();
  c.fill();
  c.fillStyle = '#1d2b36';
  c.beginPath();
  c.arc(-9, -2, 2.4, 0, Math.PI * 2);
  c.fill();
  c.restore();
}
